/**
 * Applicant Form_Renderer — inline-validation messaging helpers
 * (application-form-builder spec, task 23.2).
 *
 * Small, I/O-free helpers that turn the shared validation rules into the
 * messaging the renderer surfaces. They never re-implement a validation
 * decision: the per-field affordances and the submission-block summary both
 * derive from `fieldValidation.ts` (`validateValue` / `validateResponse`) — the
 * exact code the server runs — so client messaging and server validation stay
 * provably aligned (Req 5 ↔ Req 7).
 *
 * What lives here:
 *   - `fieldLabelMap` — a `fieldId → label` lookup over a version snapshot, so a
 *     validation error (which names a `fieldId`) can be presented with its
 *     human-readable field label in the submission-block summary.
 *   - `SummaryItem` / `summarizeBlockingErrors` — flatten React Hook Form's
 *     error object into an ordered, label-carrying list for the accessible
 *     error summary that blocks submission and names each unanswered required
 *     visible field (Req 5.3).
 *   - `longTextLimitState` — the live LONG_TEXT word/character counter state,
 *     whose "over the limit" flags come straight from `validateValue` so the
 *     affordance can never disagree with the validation that blocks submit
 *     (Req 5.4).
 *
 * Validates: Requirements 5.3, 5.4
 */

import {
  SubmissionStatus,
  validateValue,
  type FormFieldDef,
  type FormVersionSnapshot,
  type ResponseValue,
} from './pure';

// ---------------------------------------------------------------------------
// Field-label lookup
// ---------------------------------------------------------------------------

/**
 * Build a `fieldId → label` map across every page/field in the snapshot
 * (CUSTOM and BOUND alike). Used to present a validation error — which carries
 * only a `fieldId` — with its field label in the submission-block summary.
 */
export function fieldLabelMap(snapshot: FormVersionSnapshot): Map<string, string> {
  const map = new Map<string, string>();
  for (const page of snapshot.pages) {
    for (const field of page.fields) {
      map.set(field.id, field.label);
    }
  }
  return map;
}

// ---------------------------------------------------------------------------
// Submission-block summary
// ---------------------------------------------------------------------------

/** One row in the accessible submission-block summary. */
export interface SummaryItem {
  /** The offending field id — the anchor target the summary links to. */
  fieldId: string;
  /** The field's label, when resolvable from the snapshot; falls back to the id. */
  label: string;
  /** The validation message to present (e.g. `Field "X" is required.`). */
  message: string;
}

/**
 * The minimal shape this module reads from a React Hook Form `FieldErrors`
 * entry. RHF stores `{ message, type, ... }` per field path; only the message
 * is needed for the summary.
 */
export interface FormErrorLike {
  message?: string;
}

/**
 * Flatten React Hook Form's per-field errors into an ordered, label-carrying
 * list for the submission-block summary (Req 5.3). The form's Zod schema is
 * `deriveZodSchema`, which delegates to `validateResponse`, so every entry here
 * corresponds to a real `FieldValidationError` — including the `REQUIRED` errors
 * raised for each unanswered required visible field on a submit attempt.
 *
 * Entries are ordered by the snapshot's display order (the `labels` insertion
 * order) so the summary reads top-to-bottom like the form; any error whose field
 * is not in `labels` (defensive) is appended in encounter order.
 */
export function summarizeBlockingErrors(
  errors: Record<string, FormErrorLike | undefined>,
  labels: Map<string, string>,
): SummaryItem[] {
  const items: SummaryItem[] = [];
  const seen = new Set<string>();

  const push = (fieldId: string) => {
    const error = errors[fieldId];
    if (!error || seen.has(fieldId)) {
      return;
    }
    const message = error.message;
    if (typeof message !== 'string' || message.length === 0) {
      return;
    }
    seen.add(fieldId);
    items.push({ fieldId, label: labels.get(fieldId) ?? fieldId, message });
  };

  // Snapshot-ordered first, then any stragglers not present in the label map.
  for (const fieldId of labels.keys()) {
    push(fieldId);
  }
  for (const fieldId of Object.keys(errors)) {
    push(fieldId);
  }

  return items;
}

// ---------------------------------------------------------------------------
// LONG_TEXT live counter
// ---------------------------------------------------------------------------

/** The live state of a LONG_TEXT word/character counter affordance. */
export interface LongTextLimitState {
  /** Whether the field has a configured `wordLimit`. */
  hasWordLimit: boolean;
  /** Whether the field has a configured `characterLimit`. */
  hasCharLimit: boolean;
  /** The configured word limit (only meaningful when `hasWordLimit`). */
  wordLimit?: number;
  /** The configured character limit (only meaningful when `hasCharLimit`). */
  characterLimit?: number;
  /** The current word count of the entered text (display figure). */
  wordCount: number;
  /** The current character count of the entered text (display figure). */
  charCount: number;
  /** Whether the word count exceeds the limit — decided by `validateValue`. */
  overWordLimit: boolean;
  /** Whether the character count exceeds the limit — decided by `validateValue`. */
  overCharLimit: boolean;
}

/** A positive-integer limit guard (mirrors the pure module's display gate). */
function isPositiveIntegerLimit(value: number | undefined): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0;
}

/** Count words as maximal runs of non-whitespace characters (display figure). */
function countWordsForDisplay(text: string): number {
  const trimmed = text.trim();
  return trimmed.length === 0 ? 0 : trimmed.split(/\s+/).length;
}

/**
 * Compute the LONG_TEXT counter state for a field and its current value. The
 * counts are display figures; the authoritative "over the limit" flags come
 * from {@link validateValue}, so the affordance and the blocking validation can
 * never disagree (Req 5.4).
 */
export function longTextLimitState(
  field: FormFieldDef,
  value: ResponseValue | undefined,
): LongTextLimitState {
  const text = value !== undefined && value.kind === 'text' ? value.value : '';
  const hasWordLimit = isPositiveIntegerLimit(field.wordLimit);
  const hasCharLimit = isPositiveIntegerLimit(field.characterLimit);

  const errors = value !== undefined ? validateValue(field, value) : [];

  return {
    hasWordLimit,
    hasCharLimit,
    wordLimit: hasWordLimit ? field.wordLimit : undefined,
    characterLimit: hasCharLimit ? field.characterLimit : undefined,
    wordCount: countWordsForDisplay(text),
    charCount: text.length,
    overWordLimit: errors.some((e) => e.code === 'WORD_LIMIT'),
    overCharLimit: errors.some((e) => e.code === 'CHAR_LIMIT'),
  };
}

// ---------------------------------------------------------------------------
// Phase-deadline lock / phase-closed messaging
// (season-phase-deadlines Req 8, 9, 13, 16, 17)
// ---------------------------------------------------------------------------

/**
 * The Director-set manual lock override on a single Application. Mirrors the
 * transport `ManualLockOverride` value-for-value (re-declared locally so this
 * pure module carries no transport-layer import).
 */
export type ManualLockOverrideValue = 'LOCKED' | 'UNLOCKED';

/** The reason an applicant surface is presented read-only / a submit is blocked. */
export type PhaseLockKind = 'LOCKED' | 'PHASE_1_CLOSED' | 'PHASE_2_CLOSED';

/**
 * The effective, applicant-facing lock decision the renderer mirrors from the
 * server-derived fields (`effectiveLock` / `isPhase1Open` / `isPhase2Unlocked`)
 * and the Director `manualLockOverride`. When non-null the Application's fields
 * are read-only and a submit for the affected phase is prevented, and `message`
 * is the specific phase-and-Season message to surface (never a generic error).
 */
export interface PhaseLockState {
  kind: PhaseLockKind;
  /** The specific phase-closed / locked message identifying the phase and Season. */
  message: string;
}

/**
 * Render a Season label fragment for a message, e.g. `the 2026 Season`. Falls
 * back to `this Season` when no label is available so the message never dangles.
 */
export function seasonLabelText(seasonLabel?: string | null): string {
  const trimmed = seasonLabel?.trim();
  return trimmed ? `the ${trimmed}` : 'this Season';
}

/** The specific phase-closed / locked message identifying the phase and Season (R9.5). */
export function phaseLockMessage(kind: PhaseLockKind, seasonLabel?: string | null): string {
  const season = seasonLabelText(seasonLabel);
  switch (kind) {
    case 'LOCKED':
      return `Your ${season} application has been locked by a director and is now read-only. Please contact the pageant team if you need to make a change.`;
    case 'PHASE_1_CLOSED':
      return `Phase 1 (the initial application) for ${season} has closed. You can no longer submit your application for this Season.`;
    case 'PHASE_2_CLOSED':
      return `Phase 2 (supplemental materials) for ${season} has closed. You can no longer submit supplemental materials for this Season.`;
    default:
      return `${season} is no longer accepting submissions.`;
  }
}

/**
 * Mirror the server's effective-lock decision on the applicant surface from the
 * server-derived read fields, returning the reason a surface is read-only and a
 * submit is blocked — or `null` when the Application is open/editable.
 *
 * Precedence matches the server (`phaseSubmitBlock` in `responseOps.ts`):
 *   1. A Director `LOCKED` override always locks (takes precedence over dates).
 *   2. On the Phase 1 surface (not yet APPROVED) a closed Phase 1
 *      (`isPhase1Open === false`) blocks the initial submission (R9.1, R9.2).
 *   3. On the Phase 2 surface (APPROVED) a closed Phase 2 window
 *      (`isPhase2Unlocked === false`) blocks the supplemental submission
 *      (R9.3, R9.4).
 *   4. Otherwise a truthy server-derived `effectiveLock` (e.g. a date-based
 *      close the phase-specific flags did not already explain) locks the
 *      Application read-only.
 *
 * The server remains authoritative — this is the client mirror (R16.5, R17.2),
 * so the flags come straight from `getMyApplicationResponse`.
 */
export function derivePhaseLockState(input: {
  submissionStatus: SubmissionStatus;
  effectiveLock?: boolean | null;
  isPhase1Open?: boolean | null;
  isPhase2Unlocked?: boolean | null;
  manualLockOverride?: ManualLockOverrideValue | null;
  seasonLabel?: string | null;
}): PhaseLockState | null {
  const { submissionStatus, effectiveLock, isPhase1Open, isPhase2Unlocked, manualLockOverride } =
    input;

  const build = (kind: PhaseLockKind): PhaseLockState => ({
    kind,
    message: phaseLockMessage(kind, input.seasonLabel),
  });

  // 1. Director LOCKED override always wins.
  if (manualLockOverride === 'LOCKED') {
    return build('LOCKED');
  }
  // A Director UNLOCKED override reopens the whole Application (never blocked).
  if (manualLockOverride === 'UNLOCKED') {
    return null;
  }

  const isApproved = submissionStatus === SubmissionStatus.APPROVED;

  // 2. Phase 1 surface: a closed intake window blocks the initial submission.
  if (!isApproved && isPhase1Open === false) {
    return build('PHASE_1_CLOSED');
  }
  // 3. Phase 2 surface: a closed Phase 2 window blocks supplemental submission.
  if (isApproved && isPhase2Unlocked === false) {
    return build('PHASE_2_CLOSED');
  }
  // 4. Any remaining server-derived effective lock (defensive fallback).
  if (effectiveLock === true) {
    return build('LOCKED');
  }
  return null;
}

/**
 * Classify a server rejection (an `ApplicationFormServiceError` surfaced through
 * the transport as an `Error` message) as a phase-closed / locked reason so the
 * surface can render the SPECIFIC phase-and-Season message rather than the raw
 * server text or a generic error (R9.5). Returns `null` for any other error.
 *
 * The backend `errorType` (`PhaseClosed` / `Locked`) is collapsed to a message
 * string by the transport, so this matches the stable server message text; an
 * explicit `errorType` is honored first when a transport does surface it.
 */
export function classifyPhaseLockError(
  message?: string | null,
  errorType?: string | null,
): PhaseLockKind | null {
  if (errorType === 'Locked') return 'LOCKED';
  const text = (message ?? '').toLowerCase();
  if (!text) return null;
  if (text.includes('locked by a director')) return 'LOCKED';
  if (text.includes('phase 2 submission window')) return 'PHASE_2_CLOSED';
  if (
    text.includes('intake for this season has ended') ||
    text.includes('phase 1 submission window')
  ) {
    return 'PHASE_1_CLOSED';
  }
  return null;
}
